import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';
import { useGetPointsDestroyedQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from './TimeRangeDropdown';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

const SEGMENT_COLORS = [
  '#8409D3',
  '#8D37A7',
  '#C769EB',
  '#DD5BEA',
  '#E02CDC',
  '#9941B4'
];

const PointsDestroyedChart: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error } = useGetPointsDestroyedQuery({ period });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const chartData = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((point) => ({
      date: point.date,
      ...point.segments
    }));
  }, [data]);

  const segments = data?.allSegments || [];

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>Points Destroyed</h2>
          <p className="chart-subtitle">Points removed from the ecosystem by type</p>
        </div>
        <div className="chart-header-right">
          <div className="chart-legend-multi">
            {segments.slice(0, 3).map((segment, index) => (
              <div key={segment} className="chart-legend">
                <span
                  className="legend-dot"
                  style={{ backgroundColor: SEGMENT_COLORS[index % SEGMENT_COLORS.length] }}
                />
                <span className="legend-label">{segment}</span>
              </div>
            ))}
            {segments.length > 3 && (
              <span className="legend-label">+{segments.length - 3} more</span>
            )}
          </div>
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">Failed to load data</div>
        ) : data ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
                interval={chartData.length > 60 ? Math.floor(chartData.length / 12) : 0}
              />
              <YAxis
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #2a2a2a',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                labelFormatter={(label) => formatDate(label)}
                formatter={(value) => [`${Number(value).toLocaleString()} points`]}
              />
              <Legend
                wrapperStyle={{ paddingTop: '10px' }}
                formatter={(value) => <span style={{ color: '#999' }}>{value}</span>}
              />
              {segments.map((segment, index) => (
                <Bar
                  key={segment}
                  dataKey={segment}
                  stackId="destroyed"
                  fill={SEGMENT_COLORS[index % SEGMENT_COLORS.length]}
                  radius={index === segments.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  name={segment}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : null}
      </div>

      {data && (
        <div className="chart-summary">
          Total destroyed: {Object.entries(data.totalBySegment)
            .map(([segment, count]) => `${segment}: ${count.toLocaleString()}`)
            .join(' | ')}
        </div>
      )}
    </div>
  );
};

export default PointsDestroyedChart;
